/**
 * 자동 생성 파일 — 직접 고치지 마세요.
 * 생성: node scripts/gen-menu-code-defaults.js
 *
 * 사이드바 코드(CRMLayout·AutomationLayout 의 MENU_SECTIONS)에 박혀 있는 메뉴별
 * 이름·아이콘 이름·경로. 메뉴 관리 화면이 "지금 설정된 값"을 입력칸에 채워 보여 주고,
 * 다른 대메뉴로 옮겨 간 메뉴를 그쪽 사이드바가 그리는 데 쓴다(menuPlacement).
 * 코드와 어긋나면 src/data/__tests__/menuCodeDefaults.test.ts 가 실패한다.
 */

export type MenuCodeDefault = { label?: string; icon?: string; path?: string; minRole?: string };

/** 코드 사이드바가 실제로 쓰는 아이콘 이름들 — 선택 목록의 바탕이 된다. */
export const CODE_ICON_NAMES: string[] = [
  "BookOpen",
  "Boxes",
  "Braces",
  "Briefcase",
  "Building2",
  "Calculator",
  "CalendarDays",
  "CalendarRange",
  "ClipboardList",
  "Database",
  "FileAudio",
  "FileSignature",
  "FileText",
  "Files",
  "FlaskConical",
  "FolderKanban",
  "FolderOpen",
  "GanttChartSquare",
  "GitBranch",
  "Handshake",
  "Headset",
  "Inbox",
  "KanbanSquare",
  "LayoutDashboard",
  "LayoutGrid",
  "LayoutTemplate",
  "ListChecks",
  "Mail",
  "Map",
  "Megaphone",
  "MessageSquare",
  "ReceiptText",
  "Send",
  "ShieldCheck",
  "SlidersHorizontal",
  "Store",
  "Target",
  "Truck",
  "Warehouse",
  "Zap",
];

export const MENU_CODE_DEFAULTS: Record<string, MenuCodeDefault> = {
  "automation:db": { label: "DB 관리", minRole: "admin" },
  "automation:db:db-document-data": { label: "문서 DB 관리", icon: "Database", path: "/automation/db/document-data" },
  "automation:equipment-catalog": { label: "설비 카탈로그" },
  "automation:equipment-catalog:equipment-catalog": { label: "설비 카탈로그", icon: "Boxes", path: "/automation/equipment-catalog" },
  "automation:field-research": { label: "현장 리서치" },
  "automation:field-research:events": { label: "행사", icon: "Map", path: "/crm/field-research" },
  "automation:field-research:questions": { label: "질문 관리", icon: "MessageSquare", path: "/crm/field-research/questions" },
  "automation:guides": { label: "가이드" },
  "automation:guides:diagnosis-logic": { label: "진단 로직", icon: "ClipboardList", path: "/automation/diagnosis/logic" },
  "automation:guides:invoice-guide": { label: "인보이스 가이드", icon: "BookOpen", path: "/automation/invoice-guide" },
  "automation:guides:simulator": { label: "시뮬레이터", icon: "Zap", path: "/automation/simulator" },
  "automation:operations": { label: "운영 관리", minRole: "admin" },
  "automation:operations:db-mou-status": { label: "MOU 현황 관리", icon: "FileText", path: "/automation/db/mou-status" },
  "automation:planning": { label: "서비스 기획" },
  "automation:planning-management": { label: "기획 관리" },
  "automation:planning-management:planning-documents": { label: "기획 문서 관리", icon: "FileText", path: "/planning/documents" },
  "automation:planning:roi-analysis": { label: "ROI 분석 도구", icon: "ClipboardList", path: "/planning/roi-analysis" },
  "automation:planning:sales-pipeline": { label: "Sales Pipeline", icon: "ClipboardList", path: "/planning/sales-pipeline" },
  "automation:poc": { label: "POC" },
  "automation:poc:care-desk": { label: "Care Desk 접수·스케줄", icon: "Headset", path: "/automation/poc/care-desk" },
  "automation:poc:hwp-form": { label: "HWP 서식 편집·출력", icon: "FileSignature", path: "/automation/poc/hwp-form" },
  "automation:poc:meeting-notes": { label: "AI 회의록 자동화", icon: "FileAudio", path: "/automation/poc/meeting-notes" },
  "automation:poc:mro-arena": { label: "MRO 편집샵(Vault)", icon: "Store", path: "/crm/poc/mro" },
  "automation:poc:mwms-api-test": { label: "MWMS API 테스트", icon: "Send", path: "/automation/poc/mwms-api-test" },
  "automation:poc:proposal": { label: "제안서 빌더", icon: "FileText", path: "/automation/poc/proposal" },
  "automation:poc:rfq": { label: "견적 요청 (RFQ)", icon: "Send", path: "/automation/poc/rfq" },
  "automation:poc:task-management": { label: "Task 관리", icon: "ListChecks", path: "/automation/poc/tasks" },
  "automation:poc:twin-studio": { label: "디지털 트윈 스튜디오 (TEST)", icon: "FlaskConical", path: "/automation/poc/twin-studio" },
  "automation:poc:warehouse-rack": { label: "창고 3D Rack", icon: "Warehouse", path: "/automation/poc/warehouse-rack" },
  "automation:poc:wms-billing-sim": { label: "WMS Billing 시뮬레이터", icon: "Calculator", path: "/automation/poc/wms-billing-simulation" },
  "automation:settlement": { label: "브랜드 정산", minRole: "admin" },
  "automation:settlement:brand501": { label: "브랜드501", icon: "ReceiptText", path: "/settlement/brand501" },
  "automation:vault": { label: "Vault", minRole: "admin" },
  "automation:vault:vault-content": { label: "관리", icon: "Database", path: "/automation/vault/content" },
  "crm:document-automation": { label: "DOCUMENTS", icon: "Files" },
  "crm:document-automation:3d-simulation": { label: "Digital Twin Sandbox", icon: "Braces", path: "/crm/3d-simulation" },
  "crm:document-automation:documents": { label: "문서 템플릿", icon: "Files", path: "/crm/documents/quote" },
  "crm:document-automation:logistics-data-lab": { label: "Site Data Analysis", icon: "Truck", path: "/crm/logistics-data-lab" },
  "crm:document-automation:proposal": { label: "Proposal", icon: "LayoutTemplate", path: "/crm/document-automation/proposal" },
  "crm:marketing": { label: "Marketing", icon: "Megaphone" },
  "crm:marketing:event": { label: "Event(참석자)", icon: "CalendarRange", path: "/crm/marketing/events" },
  "crm:marketing:marketing-dashboard": { label: "Marketing Dashboard", icon: "LayoutDashboard", path: "/crm/marketing/dashboard" },
  "crm:opportunity": { label: "Opportunity", icon: "Target" },
  "crm:opportunity:pipeline": { label: "영업 기회", icon: "KanbanSquare", path: "/crm/opportunities" },
  "crm:opportunity:pipeline-settings": { label: "Pipeline Settings", icon: "GitBranch", path: "/crm/pipeline-settings" },
  "crm:opportunity:wbs": { label: "WBS", icon: "GanttChartSquare", path: "/crm/wbs" },
  "crm:overview": { label: "Overview", icon: "LayoutGrid" },
  "crm:overview:automation-roadmap": { label: "Automation Roadmap", icon: "Map", path: "/crm/automation-roadmap" },
  "crm:overview:calendar": { label: "Calendar", icon: "CalendarDays", path: "/crm/calendar" },
  "crm:overview:dashboard": { label: "Dashboard", icon: "LayoutDashboard", path: "/crm/dashboard" },
  "crm:overview:opportunity-overview": { label: "Opportunity Overview", icon: "ClipboardList", path: "/crm/opportunity-overview" },
  "crm:project": { label: "Project", icon: "FolderKanban" },
  "crm:project:project-pipeline-settings": { label: "Pipeline Settings", icon: "GitBranch", path: "/crm/project-pipeline-settings" },
  "crm:project:projects": { label: "Projects", icon: "FolderKanban", path: "/crm/projects" },
  "crm:resources": { label: "Resources", icon: "FolderOpen" },
  "crm:resources:company-assets": { label: "회사 자료", icon: "FolderOpen", path: "/crm/company-assets" },
  "crm:resources:crm-files": { label: "CRM 파일", icon: "FolderOpen", path: "/crm/files" },
  "crm:sales": { label: "Sales", icon: "Briefcase" },
  "crm:sales:leads": { label: "Lead Inbox", icon: "Inbox", path: "/crm/leads" },
  "crm:sales:mailbox": { label: "Mailbox", icon: "Mail", path: "/crm/mailbox" },
  "crm:sales:partner-analysis": { label: "Partner Analysis", icon: "SlidersHorizontal", path: "/crm/partners/analysis" },
  "crm:sales:partner-verification": { label: "Partner Verification", icon: "ShieldCheck", path: "/crm/partner-verification" },
  "crm:sales:partners": { label: "Partners", icon: "Handshake", path: "/crm/partners" },
  "crm:sales:relationships": { label: "Relationships", icon: "Building2", path: "/crm/relationships" },
  "crm:template": { label: "Template", icon: "LayoutTemplate", minRole: "admin" },
  "crm:template:communication-templates": { label: "Communication Templates", icon: "LayoutTemplate", path: "/crm/communication-templates" },
  "crm:template:email-templates": { label: "Email", icon: "Mail", path: "/crm/email-templates" },
  "crm:template:slack-templates": { label: "Slack", icon: "MessageSquare", path: "/crm/slack-templates" },
  "crm:test": { label: "Test", icon: "FlaskConical", minRole: "super_admin" },
  "crm:test:email-send": { label: "Email Send", icon: "Mail", path: "/crm/automation/email-test" },
  "crm:test:lead-api": { label: "Lead API(VAULT)", icon: "FlaskConical", path: "/crm/lead-test" },
  "crm:test:proposal-form-sample": { label: "제안서 양식 샘플", icon: "FileText", path: "/crm/proposal-form-sample" },
};

/** 사이드바에 그려지는 순서 그대로 — 관리 화면의 행 순서가 실제 메뉴와 같아야 한다. */
export const MENU_CODE_ORDER: string[] = [
  "crm:overview",
  "crm:overview:dashboard",
  "crm:overview:calendar",
  "crm:overview:opportunity-overview",
  "crm:overview:automation-roadmap",
  "crm:sales",
  "crm:sales:leads",
  "crm:sales:mailbox",
  "crm:sales:relationships",
  "crm:sales:partners",
  "crm:sales:partner-analysis",
  "crm:sales:partner-verification",
  "crm:marketing",
  "crm:marketing:marketing-dashboard",
  "crm:marketing:event",
  "crm:opportunity",
  "crm:opportunity:pipeline",
  "crm:opportunity:wbs",
  "crm:opportunity:pipeline-settings",
  "crm:project",
  "crm:project:projects",
  "crm:project:project-pipeline-settings",
  "crm:document-automation",
  "crm:document-automation:documents",
  "crm:document-automation:proposal",
  "crm:document-automation:3d-simulation",
  "crm:document-automation:logistics-data-lab",
  "crm:resources",
  "crm:resources:crm-files",
  "crm:resources:company-assets",
  "crm:template",
  "crm:template:communication-templates",
  "crm:template:email-templates",
  "crm:template:slack-templates",
  "crm:test",
  "crm:test:email-send",
  "crm:test:lead-api",
  "crm:test:proposal-form-sample",
  "automation:planning-management",
  "automation:planning-management:planning-documents",
  "automation:equipment-catalog",
  "automation:equipment-catalog:equipment-catalog",
  "automation:field-research",
  "automation:field-research:events",
  "automation:field-research:questions",
  "automation:vault",
  "automation:vault:vault-content",
  "automation:operations",
  "automation:operations:db-mou-status",
  "automation:db",
  "automation:db:db-document-data",
  "automation:guides",
  "automation:guides:diagnosis-logic",
  "automation:guides:invoice-guide",
  "automation:guides:simulator",
  "automation:planning",
  "automation:planning:sales-pipeline",
  "automation:planning:roi-analysis",
  "automation:settlement",
  "automation:settlement:brand501",
  "automation:poc",
  "automation:poc:warehouse-rack",
  "automation:poc:twin-studio",
  "automation:poc:mwms-api-test",
  "automation:poc:wms-billing-sim",
  "automation:poc:meeting-notes",
  "automation:poc:care-desk",
  "automation:poc:rfq",
  "automation:poc:hwp-form",
  "automation:poc:task-management",
  "automation:poc:proposal",
  "automation:poc:mro-arena",
];
